import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageSelector } from './LanguageSelector';
import { ProfileModal } from './ProfileModal';
import { SubscriptionManager } from './SubscriptionManager';
import { GitHubIntegration } from './GitHubIntegration';
import { Menu, X, User, LogOut, LayoutDashboard, CreditCard, ChevronDown, Settings, Crown, Github } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f913202c-f556-4e67-8289-1cc1bbfe81d7/artifacts/l0uotybx_Logo%20de%20claudtexa%20con%20fondo%20transparante.png";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showGitHubModal, setShowGitHubModal] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  };

  const navLinks = [
    { href: '/#services', label: t('nav.services') },
    { href: '/pricing', label: t('nav.pricing') },
    { href: '/#about', label: t('nav.about') },
    { href: '/#contact', label: t('nav.contact') },
  ];

  const isActive = (href) => {
    if (href.startsWith('/#')) {
      return location.pathname === '/' && location.hash === href.substring(1);
    }
    return location.pathname === href;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 header-glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src={LOGO_URL} alt="CloudTexa" className="h-14 lg:h-16 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`text-sm font-medium transition-colors duration-200 ${
                  isActive(link.href) ? 'text-[#FF6B00]' : 'text-gray-300 hover:text-white'
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden lg:flex items-center gap-4">
            <LanguageSelector />
            
            {user ? (
              <div className="flex items-center gap-4">
                <Link
                  to="/dashboard"
                  className="flex items-center gap-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
                  data-testid="nav-dashboard"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  {t('nav.dashboard')}
                </Link>
                <div className="flex items-center gap-3 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                  <CreditCard className="w-4 h-4 text-[#FF6B00]" />
                  <span className="text-sm font-semibold text-white">{user.credits}</span>
                </div>
                
                {/* User Menu Dropdown */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-white/20 transition-colors"
                    data-testid="user-menu-btn"
                  >
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF6B00] to-[#FF8533] flex items-center justify-center">
                      <span className="text-xs font-bold text-white">{user.name?.charAt(0).toUpperCase()}</span>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  <AnimatePresence>
                    {userMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute right-0 top-full mt-2 w-56 rounded-xl bg-[#0A0A0A] border border-white/10 shadow-2xl overflow-hidden z-50"
                      >
                        <div className="p-3 border-b border-white/10">
                          <p className="text-white font-medium">{user.name}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                        <div className="p-1">
                          <button
                            onClick={() => { setShowProfileModal(true); setUserMenuOpen(false); }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                            data-testid="profile-btn"
                          >
                            <Settings className="w-4 h-4" />
                            <span className="text-sm">Editar perfil</span>
                          </button>
                          <button
                            onClick={() => { setShowSubscriptionModal(true); setUserMenuOpen(false); }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                            data-testid="subscription-btn"
                          >
                            <Crown className="w-4 h-4" />
                            <span className="text-sm">Suscripción</span>
                          </button>
                          <button
                            onClick={() => { setShowGitHubModal(true); setUserMenuOpen(false); }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                            data-testid="github-btn"
                          >
                            <Github className="w-4 h-4" />
                            <span className="text-sm">GitHub</span>
                          </button>
                          <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                            data-testid="nav-logout"
                          >
                            <LogOut className="w-4 h-4" />
                            <span className="text-sm">Cerrar sesión</span>
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/login"
                  className="text-sm font-medium text-gray-300 hover:text-white transition-colors"
                  data-testid="nav-login"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  className="btn-primary px-5 py-2 rounded-lg text-sm"
                  data-testid="nav-register"
                >
                  {t('nav.register')}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-gray-300 hover:text-white"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden mobile-menu border-t border-white/5"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-base font-medium text-gray-300 hover:text-white py-2"
                >
                  {link.label}
                </a>
              ))}
              
              <div className="pt-4 border-t border-white/10">
                <LanguageSelector />
              </div>
              
              {user ? (
                <div className="pt-4 space-y-3">
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 text-base font-medium text-gray-300 hover:text-white py-2"
                  >
                    <LayoutDashboard className="w-5 h-5" />
                    {t('nav.dashboard')}
                  </Link>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <CreditCard className="w-4 h-4" />
                    {user.credits} {t('pricing.credits')}
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 text-base font-medium text-gray-400 hover:text-white py-2"
                  >
                    <LogOut className="w-5 h-5" />
                    {t('nav.logout')}
                  </button>
                </div>
              ) : (
                <div className="pt-4 space-y-3">
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-base font-medium text-gray-300 hover:text-white py-2"
                  >
                    {t('nav.login')}
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block btn-primary px-5 py-3 rounded-lg text-center"
                  >
                    {t('nav.register')}
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Profile Modal */}
      <ProfileModal isOpen={showProfileModal} onClose={() => setShowProfileModal(false)} />
      
      {/* Subscription Modal */}
      <SubscriptionManager isOpen={showSubscriptionModal} onClose={() => setShowSubscriptionModal(false)} />
      
      {/* GitHub Modal */}
      <GitHubIntegration isOpen={showGitHubModal} onClose={() => setShowGitHubModal(false)} />
    </header>
  );
};
